import { FC } from 'react';
import {
  Autocomplete,
  Box,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Select from '../../components/Select';
import MultiSelectChips, { ChipOption } from '../../components/MultiSelectChips';
import FormRow from '../../components/FormRow';
import { ON_PRIMARY_FIELD_SX } from '../../components/fieldStyles';
import { ROLE_ADMIN, ROLE_USER } from '../../constants';
import { colors, fontWeights } from '../../theme/tokens';
import { ROLE_LABELS } from '../UsersList/types';
import { UserFormErrors, UserFormValues } from './types';

// The body of the "Ajouter un utilisateur" modal (XD screen 9), also
// used to modify.
//
// Desktop: two columns. Left, the identity (Prénom, Nom, Email,
// Fonction, Rôle) with the labels to the left of the field and right-aligned,
// as in the XD. Right, Service (dropdown + ⊕ + chips),
// Autorisations (three switches) and Pour le compte de (same pattern).
//
// The columns collapse below 900 px, not below `tablet` (768): between the
// two the dialog is ~700 px wide and two label-left columns left the fields
// a hundred-odd pixels, with clipped ⊕ buttons and truncated placeholders.
// A single column with side labels fits comfortably there. The value is
// literal rather than the `md` token, which the theme marks as legacy.
//
// Mobile (ROADMAP.md, Part 2): single column in the order Prénom, Nom, Email,
// Fonction, Rôle, Service, Autorisations, Pour le compte de — which is the DOM
// order, so stacking the columns is enough. The labels move above
// the field: on the left they would leave the field too narrow.
//
// The `name`s of the fields (`first_name`, `last_name`, `email`, `can_*`) are
// the usual ones: the Cypress specs use them.
//
// The label + field row is components/FormRow, shared with the archiving
// wizard.
//
// `readOnly` is the user record (`/view-user/:id` and "Mon profil"): the
// same fields, disabled, the switches read-only and the
// chips without "✕", plus the "Dernière connexion" row (`lastLogin`, already
// formatted), which is not edited.

type Props = {
  values: UserFormValues;
  errors: UserFormErrors;
  onChange: (patch: Partial<UserFormValues>) => void;
  serviceOptions: ChipOption[];
  userOptions: ChipOption[];
  functionSuggestions: string[];
  disabled?: boolean;
  readOnly?: boolean;
  /** Read-only: "Dernière connexion", already formatted. */
  lastLogin?: string;
};

const SWITCHES: Array<{
  key:
    | 'can_issue_document'
    | 'can_issue_unsecure_document'
    | 'can_archive_secure_document';
  label: string;
}> = [
  { key: 'can_issue_document', label: 'Émettre un document sécurisé' },
  {
    key: 'can_issue_unsecure_document',
    label: 'Émettre un document non sécurisé',
  },
  { key: 'can_archive_secure_document', label: 'Archiver un document' },
];

const UserFormFields: FC<Props> = ({
  values,
  errors,
  onChange,
  serviceOptions,
  userOptions,
  functionSuggestions,
  disabled = false,
  readOnly = false,
  lastLogin,
}) => {
  const inert = disabled || readOnly;

  const textField = (
    key: 'firstname' | 'lastname' | 'email',
    name: string,
    placeholder: string,
    type = 'text',
  ) => (
    <TextField
      id={`user-${name}`}
      name={name}
      type={type}
      size="small"
      fullWidth
      value={values[key]}
      onChange={(e) => onChange({ [key]: e.target.value })}
      error={Boolean(errors[key])}
      placeholder={placeholder}
      disabled={inert}
      sx={ON_PRIMARY_FIELD_SX}
    />
  );

  return (
    <Box
      sx={(theme) => ({
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        columnGap: 5,
        rowGap: 2.5,
        [theme.breakpoints.down(900)]: {
          gridTemplateColumns: 'minmax(0, 1fr)',
          rowGap: 2,
        },
      })}
    >
      {/* Left column — identity */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <FormRow label="Prénom" htmlFor="user-first_name" error={errors.firstname}>
          {textField('firstname', 'first_name', 'Saisissez votre prénom')}
        </FormRow>
        <FormRow label="Nom" htmlFor="user-last_name" error={errors.lastname}>
          {textField('lastname', 'last_name', 'Saisissez votre nom')}
        </FormRow>
        <FormRow label="Email" htmlFor="user-email" error={errors.email}>
          {textField('email', 'email', 'Saisissez votre email', 'email')}
        </FormRow>
        <FormRow label="Fonction" htmlFor="user-function">
          <Autocomplete
            freeSolo
            // Same rule as the wizard's "Émetteur": `freeSolo` hides the
            // dropdown arrow, and the field should look like a dropdown
            // whenever it has suggestions to open.
            forcePopupIcon={functionSuggestions.length > 0}
            id="user-function"
            options={functionSuggestions}
            inputValue={values.function}
            onInputChange={(_, value) => onChange({ function: value })}
            disabled={inert}
            renderInput={(params) => (
              <TextField
                {...params}
                name="function"
                size="small"
                placeholder="Saisissez votre fonction"
                sx={ON_PRIMARY_FIELD_SX}
              />
            )}
          />
        </FormRow>
        <FormRow label="Rôle" htmlFor="user-role" error={errors.role}>
          <Select
            id="user-role"
            name="role"
            size="small"
            fullWidth
            displayEmpty
            value={values.role}
            onChange={(e) =>
              onChange({ role: e.target.value as UserFormValues['role'] })
            }
            error={Boolean(errors.role)}
            disabled={inert}
            renderValue={(selected: unknown) =>
              selected ? (
                ROLE_LABELS[selected as 'ADMIN' | 'USER']
              ) : (
                <span style={{ color: colors.hint }}>Sélectionnez</span>
              )
            }
            inputProps={{ 'aria-label': 'Rôle' }}
            sx={ON_PRIMARY_FIELD_SX}
          >
            <MenuItem value={ROLE_ADMIN}>{ROLE_LABELS.ADMIN}</MenuItem>
            <MenuItem value={ROLE_USER}>{ROLE_LABELS.USER}</MenuItem>
          </Select>
        </FormRow>
        {readOnly && lastLogin !== undefined && (
          <FormRow label="Dernière connexion" id="user-last-login-label">
            <Typography
              data-testid="user-last-login"
              aria-labelledby="user-last-login-label"
              sx={{ color: colors.white, py: 1, fontWeight: fontWeights.medium }}
            >
              {lastLogin}
            </Typography>
          </FormRow>
        )}
      </Box>

      {/* Right column — services, authorisations, delegation */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <FormRow
          label="Service"
          id="user-services-label"
          alignTop
          error={errors.services}
        >
          <MultiSelectChips
            label="Service"
            name="services"
            placeholder="Sélectionnez le service"
            options={serviceOptions}
            value={values.services}
            onChange={(services) => onChange({ services })}
            readOnly={readOnly}
          />
        </FormRow>

        <FormRow label="Autorisations" id="user-permissions-label" alignTop>
          <Box
            role="group"
            aria-labelledby="user-permissions-label"
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
          >
            {SWITCHES.map((item) => (
              <FormControlLabel
                key={item.key}
                control={
                  <Switch
                    name={item.key}
                    color="secondary"
                    checked={values[item.key]}
                    // Read-only has no `onChange`: controlled and without a
                    // handler, the switch does not change state.
                    onChange={
                      readOnly
                        ? undefined
                        : (e) => onChange({ [item.key]: e.target.checked })
                    }
                    readOnly={readOnly}
                    inputProps={{ 'aria-readonly': readOnly || undefined }}
                    disabled={disabled}
                    sx={{
                      '& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb':
                        { backgroundColor: colors.white },
                      '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track':
                        { backgroundColor: colors.white, opacity: 0.5 },
                    }}
                  />
                }
                label={item.label}
                sx={{
                  m: 0,
                  color: colors.white,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    fontWeight: fontWeights.regular,
                  },
                }}
              />
            ))}
          </Box>
        </FormRow>

        <FormRow label="Pour le compte de" id="user-delegates-label" alignTop>
          <MultiSelectChips
            label="Pour le compte de"
            name="users"
            placeholder="Sélectionnez un utilisateur"
            options={userOptions}
            value={values.users}
            onChange={(users) => onChange({ users })}
            readOnly={readOnly}
          />
        </FormRow>
      </Box>
    </Box>
  );
};

export default UserFormFields;
